import { LucideMail, LucideX } from "lucide-react";
import Modal from "../../../../components/generic/Modal";
import UserAvatar from "../../../../components/generic/UserAvatar";
import { INote } from "../../../../shared/type/list.type";
import { IUser } from "../../../../shared/type/user.type";
import { useApi } from "../../../../hooks/api.hook";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import useObjectState from "../../../../hooks/object-state.hook";
import AnimLoader from "../../../../components/generic/AnimLoader";
import { useNoteCtx } from "../NotePage";
import { AuthContext } from "../../../../contexts/auth.context";

interface IBareUser {
    id: number;
    username: string;
    email: string;
    firstName: string;
    lastName: string;
}

function getName(user: IUser) {
    return (user.profile.firstName ?? user.username) + " " + (user.profile.lastName ?? '');
}

export default function UsersModal({note, onClose}: {
    note: INote;
    onClose: () => void;
}) {

    const usersService = useApi<IBareUser[]>("/user-account");
    const [userList, setUserList] = useState<IBareUser[]>([]);
    const [state, setState] = useObjectState<{
        search: string;
        foundUsers: IBareUser[];
        loading: boolean;
        selectedUser: IBareUser|undefined;
        reqLoading: boolean;
    }>({
        search: "",
        foundUsers: [],
        loading: false,
        selectedUser: undefined,
        reqLoading: false,
    });

    const ctx = useNoteCtx();
    const auth = useContext(AuthContext);

    const close = useCallback(async () => {
        if (state.reqLoading) return;
        onClose();
    }, [onClose, state.reqLoading]);


    const removeUserFromNote = useCallback(async (targetId: number) => {
        if (!note) return;

        setState({reqLoading: true});
        const res = await ctx.service?.delete("removeUser", {
            targetId
        });

        if (!!res && res.ok) {
            ctx.reload();
        }

        setState({reqLoading: false});
    }, [note]);

    const isNoteOwner = useMemo(() => {
        if (!note || !auth.user) return false;

        return note.ownerId === auth.user.id;
    }, [note, auth.user]);

    const addUserToList = useCallback(async () => {
        if (!state.selectedUser || !note) return;

        setState({reqLoading: true});

        const res = await ctx.service?.post("addUser", {
            targetId: state.selectedUser.id
        });

        if (!!res && res.ok) {
            setState({
                foundUsers: [],
                search: "",
                selectedUser: undefined
            });
            ctx.reload();
        }

        setState({reqLoading: false});
    }, [state.selectedUser, note]);

    useEffect(() => {
        setState({loading: true});
        const t = setTimeout(() => {
            if (!state.search) {
                setState({foundUsers: [], loading: false});
                return;
            }

            let finalList = userList.filter(v => v.email.toLowerCase().includes(state.search.toLowerCase()));
            finalList = finalList.filter(v => !note.users?.map(w => w.id).includes(v.id) && v.id !== note.ownerId);

            setState({loading: false, foundUsers: finalList});
        }, 500);

        return () => {
            clearTimeout(t);
            setState({loading: false});
        }
    }, [state.search]);

    useEffect(() => {
        usersService.get();
    }, []);

    useEffect(() => {
        if (!usersService.data) return;

        setUserList(usersService.data);
    }, [usersService.data]);

    return (
        <Modal title="Manage Access" id="userModal" open onClose={close}>
            <div className="border border-base-300">
                <div className="bg-base-300 p-2">
                    <p>{note.label}</p>
                    <div className="text-xs text-white/50">Owned by <UserAvatar id={note.owner.id} size="sm" className="ml-2" /> <span className="text-primary-content">{note.owner.profile.firstName ?? note.owner.username} {note.owner.profile.lastName}</span></div>
                </div>

                <div className="bg-base-200 p-2">
                    <p className="text-xs text-white/50">Shared with</p>

                    {!note.users?.length && (
                        <p className="text-white/50 mt-2">Nobody</p>
                    )}

                    {note.users?.map(user => (
                        <div key={`user_${user.id}`} className="bg-base-100 border border-base-300 p-2">
                            <div className="flex items-center gap-2">
                                <div><UserAvatar id={user.id} size="md" /></div>
                                <div className="flex-1">
                                    <p>{getName(user)}</p>
                                </div>
                                {isNoteOwner && (
                                    <div>
                                        <button disabled={state.reqLoading} onClick={() => removeUserFromNote(user.id)} className="btn btn-xs btn-circle btn-ghost"><LucideX size={16} /></button>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>

                {isNoteOwner && (
                    <div className="bg-base-300 p-2">
                        <p className="text-xs text-white/50">Add {!!note.users?.length ? "another" : "a"} user</p>

                        {!!state.selectedUser ? (
                            <div className="bg-base-200 border-base-100 border p-2 mt-2">
                                <div className="flex items-center gap-2">
                                    <div>
                                        <UserAvatar id={state.selectedUser.id} size="md" />
                                    </div>
                                    <div className="flex-1">
                                        <p>{state.selectedUser.firstName ?? state.selectedUser.username} {state.selectedUser.lastName ?? ""} <span className="text-xs text-white/50">({state.selectedUser.email})</span></p>
                                    </div>
                                    <div>
                                        <button disabled={state.reqLoading} onClick={() => setState({selectedUser: undefined})} className="btn btn-sm btn-circle btn-ghost"><LucideX size={16} /></button>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <>
                            <div className="flex items-center rounded-md overflow-hidden border-2 border-white/10">
                                <div className="bg-base-200 p-2">
                                    <LucideMail size={16} />
                                </div>
                                <div className="flex-1">
                                    <input value={state.search} onChange={e => setState({search: e.target.value})}
                                    autoComplete="off" placeholder="Email address" type="text" className="p-1 bg-transparent w-full outline-none" />
                                </div>
                                {!!state.search && (
                                    <div>
                                        <button onClick={() => setState({search: ""})} className="btn btn-xs btn-circle btn-ghost"><LucideX size={12} /></button>
                                    </div>
                                )}
                            </div>

                            {state.loading && !!state.search && (
                                <div className="flex justify-center">
                                    <AnimLoader size={32} />
                                </div>
                            )}

                            {/* <AnimLoader size={32} /> */}

                            {!state.loading && !state.foundUsers.length && !!state.search && (
                                <p className="p-2 text-xs text-white/50">No results</p>
                            )}

                            {!!state.foundUsers.length && !state.loading && (
                                <div className="bg-base-200">
                                    {state.foundUsers.map(user => (
                                        <div key={`inv-usr-${user.id}`}
                                        onClick={() => {
                                            setState({selectedUser: user})
                                        }}
                                        className="cursor-pointer hover:bg-base-100 active:bg-neutral border-b border-base-300 flex items-center gap-2 p-2">
                                            <div>
                                                <UserAvatar id={user.id} size="md" />
                                            </div>
                                            <div className="flex-1">
                                                <p>{user.firstName ?? user.username} {user.lastName ?? ""} <span className="text-xs text-white/50">({user.email})</span></p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                            </>
                        )}

                        
                    </div>
                )}
                
            </div>

            {!!state.selectedUser && (
                <div className="my-4 flex items-center justify-end gap-2">
                    <button disabled={state.reqLoading} onClick={addUserToList} className="btn btn-sm btn-primary">
                        {state.reqLoading ? <AnimLoader size={16} lineWidth={2} color="#e0e0e0" /> : "Accept"}
                    </button>
                    <button disabled={state.reqLoading} onClick={close} className="btn btn-sm btn-ghost">Cancel</button>
                </div>
            )}
        </Modal>
    );
}